import {
    BathroomMedium,
    BedroomMedium,
    LivingRoomMedium
} from '@zoopla-eng/zds-components/icons';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { Image } from '@zoopla/image';
import { FC } from 'react';
import Head from 'next/head';
import {
    featurePropertyCardWrapperStyle,
    propertyDetailsContainerStyle,
    styledCardStyle
} from '../FeaturedProperties.styles.css';
import {
    agentImageStyle,
    agentImageWrapperStyle,
    agentLinkStyle,
    featureWrapperStyle,
    iconAndTextStyle,
    propertyDetailsStyle,
    propertyImageStyle,
    styledPropertyAddressStyle,
    styledPropertyPriceStyle,
    styledPropertySubPriceStyle,
    styledPropertyTitleStyle
} from './FeaturedPropertyCard.styles.css';

export type Props = {
    property: {
        listingId: string;
        title?: string | null;
        price?: string | null;
        alternativeRentFrequencyLabel?: string | null;
        address?: string | null;
        featuredType?: string | null;
        branch?: {
            logoUrl?: string | null;
            name?: string | null;
            phone?: string | null;
        } | null;
        listingUris?: {
            detail?: string | null;
        } | null;
        features?:
            | ({
                  iconId?: string | null;
                  content?: number | null;
              } | null)[]
            | null;
        image?: {
            src?: string | null;
            responsiveImgList?:
                | ({ src?: string | null; width?: number | null } | null)[]
                | null;
        } | null;
    };
    onClick?: () => void;
    searchIdentifier: string;
};

const featureMap = new Map([
    ['chair', <LivingRoomMedium />],
    ['bath', <BathroomMedium />],
    ['bed', <BedroomMedium />]
]);

const generateSrcSet = (
    img: { src?: string; width?: number }[] | undefined,
    modifier = ''
) => {
    if (!img) return '';
    let res = '';

    img.forEach((item, idx) => {
        // add srcset per width
        res += `${item.src + modifier} ${item.width}w`;

        // add comma at the end if not the last item
        if (idx < img.length - 1) {
            res += ', ';
        }
    });

    return res;
};

const FeaturedPropertyCard: FC<Props> = (props) => {
    const { property, onClick, searchIdentifier } = props;

    const { featuredType } = property;

    const query = new URLSearchParams({
        [featuredType as string]: '1',
        utm_content: 'featured_listing'
    });

    if (searchIdentifier) {
        query.append('search_identifier', searchIdentifier);
    }

    const queryString = query.toString();

    const detailsLink = props.property.listingUris?.detail
        ? `${props.property.listingUris?.detail}?${queryString}`
        : '#';

    const availableFeatures =
        property.features?.filter(
            (feature) =>
                typeof feature?.content === 'number' && feature.content > 0
        ) ?? [];

    /**
     * Main listing img can be incorrectly formatted as below, fix the widths to that of the width specified in img url
        {
          "width": 480,
          "src": "https://lid.zoocdn.com/354/255/1321022421cf8403d4ff91b84c29b7b4917735fa.jpg"
        },
        {
          "width": 960,
          "src": "https://lid.zoocdn.com/645/430/1321022421cf8403d4ff91b84c29b7b4917735fa.jpg"
        }
      */
    const responsiveImgList = property?.image?.responsiveImgList?.map((v) => ({
        width:
            v?.width === 480 ? 354 : v?.width === 960 ? 645 : v?.width ?? 645,
        src: v?.src ?? ''
    }));

    const srcSet = generateSrcSet(responsiveImgList);
    const webpSrcSet = generateSrcSet(responsiveImgList, ':p');

    return (
        <>
            {property.image?.src && (
                <Head>
                    {/* preload webp version of image */}
                    <link
                        rel="preload"
                        as="image"
                        href=""
                        imageSrcSet={webpSrcSet}
                        imageSizes="170px"
                    />
                </Head>
            )}
            <div className={featurePropertyCardWrapperStyle}>
                <div
                    className={styledCardStyle}
                    onClick={onClick}
                    data-testid="featured-property-card"
                >
                    {property.image?.src && (
                        <a
                            className={propertyImageStyle}
                            href={detailsLink}
                            data-testid="featured-property-card-link"
                            title="Property image"
                        >
                            <Image
                                src={property.image.src}
                                sources={[
                                    {
                                        type: 'image/webp',
                                        srcSet: webpSrcSet
                                    },
                                    {
                                        type: 'image/jpeg',
                                        srcSet: srcSet
                                    }
                                ]}
                                sizes="170px"
                                alt="Feature property"
                                important={true}
                                objectFit="cover"
                            />
                        </a>
                    )}
                    <div className={propertyDetailsContainerStyle}>
                        <a className={propertyDetailsStyle} href={detailsLink}>
                            <p
                                className={`${styledPropertyTitleStyle} ${fontGroups.knife}`}
                            >
                                {property.title}
                            </p>

                            <p
                                className={`${styledPropertyPriceStyle} ${fontGroups.whisk}`}
                            >
                                {property.price}
                            </p>
                            {property.alternativeRentFrequencyLabel && (
                                <p
                                    className={`${styledPropertySubPriceStyle} ${fontGroups.knife}`}
                                    data-testid={'sub-price'}
                                >
                                    {property.alternativeRentFrequencyLabel}
                                </p>
                            )}

                            <p
                                className={`${styledPropertyAddressStyle} ${fontGroups.knife}`}
                            >
                                {property.address}
                            </p>
                            <ul className={featureWrapperStyle}>
                                {availableFeatures.map((item, index) => {
                                    // TODO: ZDSU change how this works
                                    // backend shouldn't dictate
                                    // specific UI, layover from fullstack Perl.
                                    // Unfortunately a feature only has an iconId so for now
                                    // we'll use that as a "feature ID"
                                    const { iconId: featureId, content } =
                                        item ?? {};
                                    const icon = featureMap.get(
                                        featureId || ''
                                    );

                                    if (!icon) {
                                        return null;
                                    }

                                    return (
                                        <li
                                            key={`${index}-${featureId}`}
                                            className={iconAndTextStyle}
                                        >
                                            {icon}
                                            <p className={fontGroups.knife}>
                                                {content}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        </a>
                        <a className={agentLinkStyle} href={detailsLink}>
                            <div className={agentImageWrapperStyle}>
                                <Image
                                    src={property.branch?.logoUrl ?? ''}
                                    alt={property.branch?.name ?? ''}
                                    className={agentImageStyle}
                                    important={true}
                                />
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FeaturedPropertyCard;
